import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { PaperClipIcon } from '@heroicons/react/20/solid';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../utils/api';
import { hasRole } from '../utils/roleUtils';
import { useClient } from '../contexts/ClientContext';
import { useAssignedTo } from '../contexts/AssignedToContext';
import Select from 'react-select';
import { CloudArrowUpIcon } from "@heroicons/react/24/outline"; // or 'solid'
import { toast } from 'react-toastify';

const minimalToolbar = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
];

export default function TicketForm({
  onSuccess,
  isAdmin = false,
  priorityOptions = [],
  statusOptions = [],
  userInfo = {},
  // selectedClientId = '',
  selectedTicketId = '',
  selectedStatusId = '',
  selectedPriorityId = '',
  selectedTicketChildId = '',
  module_name = '',
  selectedIssueType = '',
  ticket = [],
  onClose = () => { },
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
}) {
  const [form, setForm] = useState({
    client: '',
    customer: '',
    module: module_name || '',
    title: '',
    priority: '',
    status: '',
    description: '',
    attachments: [],
    assigned_to: [], // Changed to array to support multi-select
    issue_type: '',
    enhancement_type: '',
    enhancement_hours: '',
    internal_cc: [], // Array for CC'd users
    recovery_steps: '', // Problem Recovery field
    restart_steps: '',  // Restart Procedure field
  });
  const { selectedClientId, setSelectedClientId } = useClient();
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [moduleOptions, setModuleOptions] = useState([]);
  const { clients: clientList } = useClient();
  const [customerOptions, setCustomerOptions] = useState([]);
  
  // Check if logged-in user is Admin (role_id = 1)
  const storedUserStr = localStorage.getItem('user');
  let storedUser = {};
  try { storedUser = storedUserStr ? JSON.parse(storedUserStr) : {}; } catch (e) {}
  const currentUser = userInfo && Object.keys(userInfo).length > 0 ? userInfo : storedUser;
  const userRoleId = Number(currentUser?.role_id ?? currentUser?.user_role_id ?? 0);
  const isAdminUser = (userRoleId === 1);

  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [isModuleLoading, setIsModuleLoading] = useState(false);
  const isEditMode = !!selectedTicketId;
  const { assignedToList, fetchAssignedToList } = useAssignedTo();
  const [showAssignee, setShowAssignee] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [sizePopupMessage, setSizePopupMessage] = useState('');

  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link']
    ]
  }), []);

  const handleAutoLink = (quill) => {
    const range = quill.getSelection();
    if (!range || range.length > 0) return;

    const index = range.index;
    if (index < 2) return;

    const text = quill.getText();
    const lastChar = text.charAt(index - 1);

    if (lastChar === ' ' || lastChar === '\n') {
      let start = index - 2;
      while (start >= 0 && text.charAt(start) !== ' ' && text.charAt(start) !== '\n') {
        start--;
      }
      start++;

      const word = text.slice(start, index - 1);
      
      const format = quill.getFormat(start, word.length);
      if (format && format.link) return;

      const urlPattern = /^(https?:\/\/|www\.)[^\s$.?#].[^\s]*$/i;
      if (urlPattern.test(word)) {
        let href = word;
        if (word.toLowerCase().startsWith('www.')) {
          href = 'https://' + word;
        }
        
        quill.formatText(start, word.length, 'link', href);
        quill.format('link', false);
      }
    }
  };

  const toTitleCase = (str) =>
    str
      ?.toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const assigneeOptions = assignedToList?.data?.map((user) => {
    const fullName = `${toTitleCase(user.first_name || '')} ${toTitleCase(user.last_name || '')}`.trim();
    const desig = user.designation_name?.trim() ? ` (${toTitleCase(user.designation_name.trim())})` : '';
    return {
      value: user.user_id,
      label: `${fullName}${desig}`
    };
  }) || [];

  // Dynamic Role 8 Code Reviewers and Role 6 Production Engineers
  const codeReviewerUsers = useMemo(() => {
    return (assignedToList?.data || []).filter(u => hasRole(u, 8));
  }, [assignedToList?.data]);

  const codeReviewerOptions = useMemo(() => {
    if (codeReviewerUsers.length > 0) {
      return codeReviewerUsers.map(u => {
        const count = Number(u.pending_code_review_count || 0);
        const baseName = `${toTitleCase(u.first_name)} ${toTitleCase(u.last_name)} (${toTitleCase(u.designation_name || 'Code Verification')})`;
        return {
          value: u.user_id,
          label: `${baseName} (${count} Pending)`,
          baseLabel: baseName,
          pendingCount: count,
        };
      });
    }
    return assigneeOptions.filter(opt => ['100', '103'].includes(String(opt.value)));
  }, [codeReviewerUsers, assigneeOptions]);

  const codeReviewerIds = useMemo(() => {
    return codeReviewerUsers.length > 0 
      ? codeReviewerUsers.map(u => String(u.user_id)) 
      : ['100', '103'];
  }, [codeReviewerUsers]);

  const codeReviewerNamesLabel = useMemo(() => {
    return codeReviewerUsers.length > 0 
      ? codeReviewerUsers.map(u => `${toTitleCase(u.first_name)} ${toTitleCase(u.last_name)}`).join(', ') 
      : 'Sanjay Madhan S, Saravanan N';
  }, [codeReviewerUsers]);

  const productionEngineerUsers = useMemo(() => {
    return (assignedToList?.data || []).filter(u => hasRole(u, 6));
  }, [assignedToList?.data]);

  const productionEngineerNamesLabel = useMemo(() => {
    return productionEngineerUsers.length > 0 
      ? productionEngineerUsers.map(u => `${toTitleCase(u.first_name)} ${toTitleCase(u.last_name)}`).join(', ') 
      : 'Gokulraj A, Gowtham N';
  }, [productionEngineerUsers]);

  const isDeployer = useMemo(() => {
    return hasRole(userInfo, 6) || hasRole(currentUser, 6);
  }, [userInfo, currentUser]);

  const isClientAdmin = useMemo(() => {
    return Boolean(
      currentUser?.is_client_admin ||
      userInfo?.is_client_admin ||
      Number(currentUser?.role_id ?? currentUser?.user_role_id) === 17 ||
      Number(userInfo?.role_id ?? userInfo?.user_role_id) === 17 ||
      (userRoleId === 2 && Array.isArray(assignedToList?.data) && assignedToList.data.length > 0)
    );
  }, [currentUser, userInfo, userRoleId, assignedToList?.data]);

  const isClientRole = useMemo(() => {
    return Boolean(
      userRoleId === 2 ||
      userRoleId === 17 ||
      Number(userInfo?.role_id) === 2 ||
      Number(userInfo?.role_id) === 17 ||
      Number(currentUser?.role_id) === 2 ||
      Number(currentUser?.role_id) === 17 ||
      isClientAdmin
    );
  }, [userRoleId, userInfo?.role_id, currentUser?.role_id, isClientAdmin]);

  const isTicketInProdMove = Number(selectedStatusId) === 17;

  const lastReviewComment = useMemo(() => {
    if (!Array.isArray(ticket?.comments)) return null;
    // Comments are DESC (newest first). Find the most recent code review action (15: Approved, 16: Rejected)
    return ticket.comments.find(c => [15, 16].includes(Number(c?.status_id)) || ['Code Review Approved', 'Code Review Rejected'].includes(c?.status));
  }, [ticket?.comments]);

  const lastReviewerName = useMemo(() => {
    if (!lastReviewComment) return '';
    if (lastReviewComment.commented_by_first_name) {
      return `${toTitleCase(lastReviewComment.commented_by_first_name)} ${toTitleCase(lastReviewComment.commented_by_last_name || '')}`.trim();
    }
    return lastReviewComment.by ? toTitleCase(lastReviewComment.by) : '';
  }, [lastReviewComment]);

  const lastReviewAction = useMemo(() => {
    if (!lastReviewComment) return 'reviewed';
    const statusId = Number(lastReviewComment?.status_id);
    if (statusId === 15 || lastReviewComment?.status === 'Code Review Approved') return 'approved';
    if (statusId === 16 || lastReviewComment?.status === 'Code Review Rejected') return 'rejected';
    return 'reviewed';
  }, [lastReviewComment]);

  // True only if the ticket has previously passed through Testing Approved (9),
  // or is already in a post-testing state. Code Review Request (14) must be hidden
  // until testing is complete.
  const hasPassedTesting = useMemo(() => {
    const postTestingStatuses = [9, 14, 15, 16, 17, 18, 19, 20];
    if (postTestingStatuses.includes(Number(selectedStatusId))) return true;
    if (!Array.isArray(ticket?.comments)) return false;
    return ticket.comments.some(
      (c) => Number(c?.status_id) === 9 || c?.status === 'Testing Approved'
    );
  }, [ticket?.comments, selectedStatusId]);

  // True only if the ticket has Code Review Approved (15) in its history,
  // or is currently in a post-code-review status. Production Move Request (17) must be hidden
  // until Code Review is Approved.
  const hasPassedCodeReview = useMemo(() => {
    const postCodeReviewStatuses = [15, 17, 18, 19, 20];
    if (postCodeReviewStatuses.includes(Number(selectedStatusId))) return true;
    if (!Array.isArray(ticket?.comments)) return false;
    return ticket.comments.some(
      (c) => Number(c?.status_id) === 15 || c?.status === 'Code Review Approved'
    );
  }, [ticket?.comments, selectedStatusId]);

  const isSecondCodeReviewer = useMemo(() => {
    if (!lastReviewComment) return false;
    const isTicketInCodeReview = Number(ticket?.status_id || selectedStatusId) === 14;
    if (!isTicketInCodeReview) return false;

    // Only actual Code Reviewers (Role 8 / Admin) should see this note
    const isCodeReviewer = hasRole(userInfo, 8) || hasRole(currentUser, 8) || Number(userInfo?.role_id || currentUser?.role_id) === 1;
    if (!isCodeReviewer) return false;

    // Do NOT show note if the current user is the same person as the last reviewer
    const currentUserId = String(userInfo?.user_id ?? currentUser?.user_id ?? currentUser?.id ?? '');
    const lastReviewerId = String(lastReviewComment?.updated_by ?? '');
    if (currentUserId && lastReviewerId && currentUserId === lastReviewerId) {
      return false;
    }

    const currentUserName = `${userInfo?.first_name || currentUser?.first_name || ''} ${userInfo?.last_name || currentUser?.last_name || ''}`.trim().toLowerCase();
    const lastReviewerFullName = `${lastReviewComment?.commented_by_first_name || ''} ${lastReviewComment?.commented_by_last_name || ''}`.trim().toLowerCase();
    if (currentUserName && lastReviewerFullName && currentUserName === lastReviewerFullName) {
      return false;
    }

    return true;
  }, [lastReviewComment, ticket?.status_id, selectedStatusId, userInfo, currentUser]);

  const getTestingRequesterId = useCallback((ticketObj) => {
    if (!ticketObj) return null;
    const comments = Array.isArray(ticketObj.comments) ? ticketObj.comments : [];

    if (comments.length > 0) {
      const status8Comments = [];
      for (let i = 0; i < comments.length; i++) {
        const c = comments[i];
        const statusId = Number(c?.status_id || 0);
        const statusName = String(c?.status || '');
        const isStatus8 = statusId === 8 || statusName === 'Testing';

        if (isStatus8) {
          status8Comments.push(c);
        } else if (status8Comments.length > 0) {
          break;
        }
      }

      if (status8Comments.length > 0) {
        // The earliest comment in the current status 8 block is the one who requested testing
        const earliestTestingComment = status8Comments[status8Comments.length - 1];
        if (earliestTestingComment?.updated_by) {
          return String(earliestTestingComment.updated_by);
        }
      }
    }

    return ticketObj?.testing_requester_id 
      || (ticketObj?.original_developer_id ? String(ticketObj.original_developer_id) : null)
      || (ticketObj?.created_by ? String(ticketObj.created_by) : null);
  }, []);

  const getCodeReviewRequesterId = useCallback((ticketObj) => {
    if (!ticketObj) return null;
    const comments = Array.isArray(ticketObj.comments) ? ticketObj.comments : [];

    if (comments.length > 0) {
      const status14Comments = [];
      for (let i = 0; i < comments.length; i++) {
        const c = comments[i];
        const statusId = Number(c?.status_id || 0);
        const statusName = String(c?.status || '');
        const isStatus14 = statusId === 14 || statusName === 'Code Review Request';

        if (isStatus14) {
          status14Comments.push(c);
        } else if (status14Comments.length > 0) {
          break;
        }
      }

      if (status14Comments.length > 0) {
        // The earliest comment in the current status 14 block is the one who requested code review
        const earliestComment = status14Comments[status14Comments.length - 1];
        if (earliestComment?.updated_by) {
          return String(earliestComment.updated_by);
        }
      }
    }

    return ticketObj?.code_review_requester_id
      || (ticketObj?.original_developer_id ? String(ticketObj.original_developer_id) : null)
      || (ticketObj?.created_by ? String(ticketObj.created_by) : null);
  }, []);

  const getProductionMoveRequesterId = useCallback((ticketObj) => {
    if (!ticketObj) return null;
    const comments = Array.isArray(ticketObj.comments) ? ticketObj.comments : [];

    if (comments.length > 0) {
      const status17Comments = [];
      for (let i = 0; i < comments.length; i++) {
        const c = comments[i];
        const statusId = Number(c?.status_id || 0);
        const statusName = String(c?.status || '');
        const isStatus17 = statusId === 17 || statusName === 'Production Move Request';

        if (isStatus17) {
          status17Comments.push(c);
        } else if (status17Comments.length > 0) {
          break;
        }
      }

      if (status17Comments.length > 0) {
        // The earliest comment in the current status 17 block is the one who requested production move
        const earliestComment = status17Comments[status17Comments.length - 1];
        if (earliestComment?.updated_by) {
          return String(earliestComment.updated_by);
        }
      }
    }

    return ticketObj?.code_review_requester_id
      || (ticketObj?.original_developer_id ? String(ticketObj.original_developer_id) : null)
      || (ticketObj?.created_by ? String(ticketObj.created_by) : null);
  }, []);

  const assigneeRef = useRef(null);
  const descriptionRef = useRef(null);

  useEffect(() => {
    if (isEditMode && ticket) {
      setForm((prev) => ({
        ...prev,
        client: ticket.client_key || ticket.client_name || '',
        customer: ticket.customer_name || '',
        module: ticket.module_name || ticket.module_id || ticket.module || module_name || '',
        internal_cc: typeof ticket.internal_cc === 'string' ? ticket.internal_cc.split(',').filter(Boolean) : (Array.isArray(ticket.internal_cc) ? ticket.internal_cc : []),
      }));
    }
  }, [isEditMode, ticket, module_name]);

  useEffect(() => {
    if (isEditMode && assigneeRef.current) {
      assigneeRef.current.focus(); //  focuses react-select input
    }
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode && descriptionRef.current) {
      descriptionRef.current.focus(); //  focuses react-select input
    }
  }, [isEditMode]);

  const [verticalHeadsOptions, setVerticalHeadsOptions] = useState([]);

  useEffect(() => {
    // Only fetch for internal tickets or admin/CA (NOT for clients)
    const fetchVerticalHeads = async () => {
      if (userInfo?.role_id && String(userInfo.role_id) === '2') {
        return;
      }
      try {
        const res = await api.get('/ticket/get-vertical-heads');
        if (res.data?.status) {
          setVerticalHeadsOptions(res.data.data.map(u => ({
            value: String(u.user_id),
            label: `${u.first_name} ${u.last_name} (${u.designation_name})`
          })));
        }
      } catch (err) {
        console.error('Failed to fetch Vertical Heads', err);
      }
    };
    fetchVerticalHeads();
  }, [userInfo]);

  useEffect(() => {
    if (descriptionRef.current) {
      const quill = descriptionRef.current.getEditor();
      quill.keyboard.addBinding({ key: 9 }, function () {
        // Prevent Quill tab behavior
        return true;
      });
    }
  }, []);

  // const handleKeyDown = (e) => {
  //   if (e.key === "Tab") {
  //     e.preventDefault();

  //     // find parent form
  //     const formEl = e.currentTarget.closest("form");
  //     if (!formEl) return;

  //     // list focusable elements inside the form
  //     const formElements = Array.from(
  //       formEl.querySelectorAll(
  //         "input, textarea, select, button, [contenteditable='true']"
  //       )
  //     ).filter(el => !el.disabled && el.tabIndex !== -1);

  //     // find currently focused element
  //     const index = formElements.indexOf(document.activeElement);

  //     if (e.shiftKey) {
  //       // Shift+Tab → go backwards
  //       const prev = formElements[index - 1];
  //       if (prev) prev.focus();
  //     } else {
  //       // Tab → go forwards
  //       const next = formElements[index + 1];
  //       if (next) next.focus();
  //     }
  //   }
  // };


  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await api.get('/ticket/get-modules', {
          params: {
            child_id: selectedTicketId === '' ? userInfo?.child_id : selectedTicketChildId
          }
        });

        if (res.data.status && Array.isArray(res.data.data)) {
          const modules = res.data.data.map((item) => ({
            value: item.id,
            label: item.module_name
          }));
          setModuleOptions(modules);

          if (modules.length === 1) {
            setForm((prev) => ({ ...prev, module: modules[0].value }));
          } else {
            setForm((prev) => ({ ...prev, module: '' }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch modules:', err);
      }
    };

    fetchModules();
    // if (userInfo?.role_id === '2' && userInfo?.cust_key) {
    //   fetchModules();
    // }
  }, [userInfo?.child_id]);


  useEffect(() => {
    if (
      selectedTicketChildId !== '' &&
      module_name &&
      moduleOptions.length > 0
    ) {
      const selectedModule = moduleOptions.find(
        (opt) => opt.label === module_name
      );
      if (selectedModule && form.module !== selectedModule.value) {
        setForm((prev) => ({
          ...prev,
          module: selectedModule.value
        }));
      }
    }
  }, [selectedTicketChildId, module_name, moduleOptions]);

  useEffect(() => {
    const roleId = Number(userInfo?.role_id ?? currentUser?.role_id ?? currentUser?.user_role_id ?? 0);
    const initialStatus = selectedTicketId ? String(selectedStatusId) : '1';

    // For customers (Role 2), if the ticket is in a state that requires their explicit action (3, 12)
    // and those states are NOT in their allowed dropdown options, we MUST reset the dropdown to ""
    // so they are forced to pick an allowed status (like IN process or Closed).
    const isCustomerActionRequired = [3, 12].includes(Number(initialStatus));
    const isNewStatusInEdit = isEditMode && String(initialStatus) === '1';
    const isCustomerInternalInProcess = isEditMode && [2, 17].includes(roleId) && [7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20].includes(Number(initialStatus));
    const finalInitialStatus = (isNewStatusInEdit || (isEditMode && [2, 17].includes(roleId) && isCustomerActionRequired))
      ? ""
      : (isCustomerInternalInProcess ? "4" : initialStatus);

    const defaultForm = {
      client: '',
      customer: '',
      module: '',
      title: '',
      priority: selectedTicketId ? selectedPriorityId : '3',
      status: finalInitialStatus,
      description: '',
      attachments: [],
      assigned_to: [], // Initialize as empty array
      issue_type: selectedIssueType ? selectedIssueType : '',
      enhancement_type: ticket?.enhancement_type ?? '',
      enhancement_hours: ticket?.enhancement_hours ?? '',
      recovery_steps: '', // Reset on ticket change
      restart_steps: '',  // Reset on ticket change
    };
    setForm(defaultForm);
  }, [isAdmin, selectedTicketId, selectedPriorityId, selectedStatusId, selectedIssueType, userInfo, currentUser, isClientAdmin, isEditMode]);

  useEffect(() => {
    setCustomerOptions([]); // clear customers when switching admin mode
  }, [isAdmin]);

  // Consolidate showAssignee logic to handle both initial load and status changes
  useEffect(() => {
    if (form.status == 3 || form.status == 5 || form.status == 12) {
      setShowAssignee(false);
    } else {
      setShowAssignee(true);
    }
  }, [form.status]);


  const getLastCommentEmployeeId = (comments, assignedUsers, fallbackAssignId) => {
    if (Array.isArray(comments) && comments.length > 0 && Array.isArray(assignedUsers) && assignedUsers.length > 0) {
      const employeeComment = comments.find((c) => {
        if (!c) return false;
        return assignedUsers.some((user) => {
          if (c.updated_by && String(user.user_id) === String(c.updated_by)) {
            return true;
          }
          if (c.by) {
            const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
            return fullName && fullName === c.by.trim().toLowerCase();
          }
          return false;
        });
      });

      if (employeeComment) {
        const matchedUser = assignedUsers.find((user) => {
          if (employeeComment.updated_by && String(user.user_id) === String(employeeComment.updated_by)) {
            return true;
          }
          if (employeeComment.by) {
            const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
            return fullName && fullName === employeeComment.by.trim().toLowerCase();
          }
          return false;
        });

        if (matchedUser) {
          return String(matchedUser.user_id);
        }
      }
    }
    return fallbackAssignId ? String(fallbackAssignId) : null;
  };

  if (!assignedToList) {
    return <div>Loading assignees...</div>; // Optional loading state
  }

  useEffect(() => {
    if ((ticket?.assign || ticket?.comments?.length) && assignedToList?.data?.length) {
      let defaultAssignee = getLastCommentEmployeeId(ticket?.comments, assignedToList?.data, ticket?.assign);

      // Auto-populate Developer for CA if ticket is IN process and assigned to CA
      const roleId = Number(userInfo?.role_id);
      if ([1, 3].includes(roleId) && Number(ticket?.status_id) === 4 && ticket?.original_developer_id) {
        // If the customer sent it as IN process to the CA, the CA needs to send it to the developer.
        // By defaulting the dropdown to the developer, CA just clicks Submit.
        if (defaultAssignee === String(userInfo?.user_id)) {
          defaultAssignee = String(ticket.original_developer_id);
        }
      }

      if (defaultAssignee) {
        setForm((prev) => ({
          ...prev,
          assigned_to: [defaultAssignee] // Wrap in array for consistency
        }));
      }
    }
  }, [ticket, assignedToList, userInfo]);

  useEffect(() => {
    if (isEditMode && selectedTicketId) {
      fetchAssignedToList?.(true, {
        ticket_id: selectedTicketId,
        child_id: selectedTicketChildId || ticket?.customer_name || userInfo?.child_id
      });
    }
  }, [isEditMode, selectedTicketId, selectedTicketChildId]);


  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Always update the changed field first
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'client' && { customer: '', module: '' }),
      ...(name === 'customer' && { module: '' }),
      ...(name === 'issue_type' && { enhancement_type: '', enhancement_hours: '' }),
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));

    if (name === 'status') {
      const selectedStatusNum = Number(value);

      // Check if user already manually selected an assignee in the dropdown
      const currentSelected = Array.isArray(form.assigned_to) && form.assigned_to.length > 0
        ? String(form.assigned_to[0])
        : (form.assigned_to ? String(form.assigned_to) : null);

      let newAssignee = undefined;

      if (selectedStatusNum === 17) {
        // Production Move Request -> automatically assign to all members in CI/CD team (Role 6)
        fetchAssignedToList?.(true);
        const deployerIds = productionEngineerUsers.length > 0
          ? productionEngineerUsers.map(u => String(u.user_id))
          : ['113', '169'];
        newAssignee = deployerIds;
      } else if (selectedStatusNum === 14) {
        // Code Review Request -> dynamically filter to employees with Role 8 (Code Verification)
        fetchAssignedToList?.(true);
        const allowedReviewerIds = codeReviewerUsers.map(u => String(u.user_id));
        if (currentSelected && (allowedReviewerIds.includes(currentSelected) || ['100', '103'].includes(currentSelected))) {
          newAssignee = currentSelected;
        } else {
          newAssignee = codeReviewerUsers.length > 0 ? String(codeReviewerUsers[0].user_id) : (assigneeOptions.some(opt => ['100', '103'].includes(String(opt.value))) ? String(assigneeOptions.find(opt => ['100', '103'].includes(String(opt.value)))?.value) : null);
        }
      } else if (selectedStatusNum === 8) {
        // Testing phase -> leave assign to empty so user selects assignee
        newAssignee = null;
      } else if (selectedStatusNum === 9 || selectedStatusNum === 10) {
        // Testing Approved (9) OR Testing Rejected (10) -> route back to developer who requested Testing
        const devId = getTestingRequesterId(ticket);
        newAssignee = devId ? String(devId) : null;
      } else if (selectedStatusNum === 4 && Number(ticket?.status_id) === 8) {
        // Moving from Testing (8) back to IN process (4) -> route back to developer who requested Testing
        const devId = getTestingRequesterId(ticket);
        newAssignee = devId ? String(devId) : null;
      } else if (selectedStatusNum === 15 || selectedStatusNum === 16) {
        // Code Review Approved (15) OR Code Review Rejected (16) -> route back to developer who requested Code Review
        const devId = getCodeReviewRequesterId(ticket);
        newAssignee = devId ? String(devId) : null;
      } else if (selectedStatusNum === 18 || selectedStatusNum === 20) {
        // Production Moved (18) OR Production Rejected (20) -> auto-assign to developer who requested the code review/move
        const devId = getProductionMoveRequesterId(ticket) || getCodeReviewRequesterId(ticket);
        newAssignee = devId ? String(devId) : null;
      } else if (selectedStatusNum === 19) {
        // Production Tested -> preserve user selection if already made, otherwise suggest assigner
        if (currentSelected) {
          newAssignee = currentSelected;
        } else {
          const devId = ticket?.code_review_requester_id || ticket?.original_developer_id || (Array.isArray(form.assigned_to) ? form.assigned_to[0] : form.assigned_to);
          let assignerId = null;

          if (Array.isArray(ticket?.comments)) {
            const assignerComment = ticket.comments.find(c => {
              const statusId = Number(c?.status_id || 0);
              const updatedBy = String(c?.updated_by || '');
              return updatedBy && updatedBy !== String(devId) && [4, 1, 3, 12].includes(statusId);
            });
            if (assignerComment) {
              assignerId = String(assignerComment.updated_by);
            }
          }

          if (!assignerId && ticket?.created_by && String(ticket.created_by) !== String(devId)) {
            assignerId = String(ticket.created_by);
          }

          if (!assignerId && ticket?.client_advocate_id && String(ticket.client_advocate_id) !== String(devId)) {
            assignerId = String(ticket.client_advocate_id);
          }

          newAssignee = assignerId || (ticket?.created_by ? String(ticket.created_by) : null);
        }

        setTimeout(() => {
          if (assigneeRef.current) {
            assigneeRef.current.focus();
          }
        }, 100);
      } else if (selectedStatusNum === 3 || selectedStatusNum === 12) {
        if (isClientAdmin) {
          // Client Admin selecting Pending verification & closure (3) or Pending Cust (12)
          // Automatically set assignee to the client (sub-client / creator)
          const clientOption = assigneeOptions.find(opt => opt.user_role_id === 'Sub-Client' || opt.is_creator);
          newAssignee = clientOption ? String(clientOption.value) : (ticket?.created_by ? String(ticket.created_by) : null);
        } else {
          const currentRoleId = Number(userInfo?.role_id);
          if ([1, 3].includes(currentRoleId)) {
            newAssignee = null;
          } else {
            newAssignee = ticket?.client_advocate_id ? String(ticket.client_advocate_id) : null;
          }
        }
      } else if (selectedStatusNum === 4 && Number(ticket?.status_id) === 9) {
        newAssignee = ticket?.client_advocate_id ? String(ticket.client_advocate_id) : null;
      } else if (selectedStatusNum === 4 && [12].includes(Number(ticket?.status_id))) {
        newAssignee = ticket?.original_developer_id ? String(ticket.original_developer_id) : null;
      } else {
        // For general status changes (like IN process), if user ALREADY selected an assignee, PRESERVE IT!
        if (currentSelected) {
          newAssignee = currentSelected;
        } else {
          newAssignee = getLastCommentEmployeeId(ticket?.comments, assignedToList?.data, ticket?.assign);
        }
      }

      if (newAssignee !== undefined) {
        setForm(prev => ({
          ...prev,
          assigned_to: Array.isArray(newAssignee) ? newAssignee : (newAssignee ? [newAssignee] : [])
        }));
      }
    }

    // console.log('showAssignee', showAssignee);

    // Fetch customers if client is selected
    if (name === 'client') {
      setModuleOptions([]);
      setCustomerOptions([]);
      setIsCustomerLoading(true);

      try {
        const res = await api.get('/ticket/get-customers', {
          params: { cust_key: value },
        });
        if (res.data.status) {
          setCustomerOptions(res.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      } finally {
        setIsCustomerLoading(false);
      }
    }

    // Fetch modules if customer is selected (only for admin)
    if (name === 'customer' && userInfo.role_id !== '2') {
      setIsModuleLoading(true);
      const selectedCustomer = customerOptions.find(c => c.cust_key === value);

      if (selectedCustomer?.modules) {
        const modules = selectedCustomer.modules.map(mod => ({
          value: mod.id,
          label: mod.module_name,
        }));
        setModuleOptions(modules);
      } else {
        setModuleOptions([]);
      }

      setIsModuleLoading(false);
    }
  };


  // const handleFileChange = (e) => {
  //   const newFiles = Array.from(e.target.files);

  //   // Avoid duplicates by name (optional)
  //   const existingNames = form.attachments.map((file) => file.name);
  //   const uniqueFiles = newFiles.filter((file) => !existingNames.includes(file.name));

  //   setForm((prev) => ({
  //     ...prev,
  //     attachments: [...prev.attachments, ...uniqueFiles],
  //   }));
  // };

  const fetchImageFromUrl = async (url) => {
    try {
      // 1. Data URLs
      if (url.startsWith('data:')) {
        const arr = url.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        const fileExt = mime.split('/')[1] || 'png';
        return new File([u8arr], `image_${Date.now()}.${fileExt}`, { type: mime });
      }

      // 2. HTTP/HTTPS URLs
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();

      if (!blob.type || !blob.type.startsWith('image/')) {
        return null;
      }

      let filename = 'downloaded_image';
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const parts = pathname.split('/');
        const lastPart = parts[parts.length - 1];
        if (lastPart && lastPart.includes('.')) {
          filename = lastPart;
        } else {
          const ext = blob.type.split('/')[1] || 'png';
          filename = `image_${Date.now()}.${ext}`;
        }
      } catch {
        const ext = blob.type.split('/')[1] || 'png';
        filename = `image_${Date.now()}.${ext}`;
      }

      return new File([blob], filename, { type: blob.type });
    } catch (err) {
      console.error('Error fetching image from URL:', err);
      return null;
    }
  };

  const addFiles = (filesToAdd) => {
    const existingNames = form.attachments.map((file) => file.name);
    let errorMsg = '';
    const validFiles = [];
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'pdf', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'zip'];
    
    // Check if logged-in user is Admin (role_id = 1)
    const storedUserStr = localStorage.getItem('user');
    let storedUser = {};
    try { storedUser = storedUserStr ? JSON.parse(storedUserStr) : {}; } catch (e) {}
    const currentUser = userInfo && Object.keys(userInfo).length > 0 ? userInfo : storedUser;
    const userRoleId = Number(currentUser?.role_id ?? currentUser?.user_role_id ?? 0);
    const isAdminUser = (userRoleId === 1);

    // Track current total size of attached files
    let currentTotalSize = form.attachments.reduce((acc, f) => acc + (f.size || 0), 0);

    for (let i = 0; i < filesToAdd.length; i++) {
      const file = filesToAdd[i];
      if (existingNames.includes(file.name)) continue;

      const extension = file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(extension)) {
        errorMsg = `Invalid file format: ${file.name}. Allowed formats: png, jpg, jpeg, pdf, docx, xls, xlsx, csv, txt, zip.`;
        break;
      }

      // If not Admin, enforce strict limits: 1.5MB total for regular files, 3MB for ZIP files
      if (!isAdminUser) {
        if (extension === 'zip') {
          if (file.size > 3 * 1024 * 1024) {
            errorMsg = `Zip file ${file.name} exceeds 3MB limit.`;
            break;
          }
          if (currentTotalSize + (file.size || 0) > 3 * 1024 * 1024) {
            errorMsg = `Total attachments size (${formatFileSize(currentTotalSize + file.size)}) exceeds the 3MB limit for zip files.`;
            break;
          }
        } else {
          // Individual regular file check
          if (file.size > 1.5 * 1024 * 1024) {
            errorMsg = `File ${file.name} exceeds 1.5MB limit. Please convert it to a zip file and upload it.`;
            break;
          }
          // Cumulative total regular files size check (1.5MB max total)
          if (currentTotalSize + (file.size || 0) > 1.5 * 1024 * 1024) {
            errorMsg = `Total attachments size (${formatFileSize(currentTotalSize + file.size)}) exceeds the 1.5MB limit. Please convert your files into a zip file and upload it.`;
            break;
          }
        }
      }

      currentTotalSize += (file.size || 0);
      validFiles.push(file);
    }

    if (errorMsg) {
      setErrors((prev) => ({
        ...prev,
        attachments: errorMsg
      }));
      setSizePopupMessage(errorMsg);
      return;
    }

    if (validFiles.length > 0) {
      setForm((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...validFiles],
      }));
      setErrors((prev) => ({
        ...prev,
        attachments: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    addFiles(newFiles);
    e.target.value = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = [];

    // 1. Try to get files from items (which includes dragged images from websites in Chromium)
    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const item = e.dataTransfer.items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
    } else if (e.dataTransfer.files) {
      // Fallback
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        files.push(e.dataTransfer.files[i]);
      }
    }

    // 2. If no files were found (or it was dragged as link/url), try fetching URL
    if (files.length === 0) {
      const uriList = e.dataTransfer.getData('text/uri-list');
      const plainText = e.dataTransfer.getData('text/plain');
      let targetUrl = '';

      if (uriList) {
        targetUrl = uriList.split('\n')[0].trim();
      } else if (plainText && plainText.startsWith('http')) {
        targetUrl = plainText.trim();
      }

      if (targetUrl) {
        const fetchedFile = await fetchImageFromUrl(targetUrl);
        if (fetchedFile) {
          files.push(fetchedFile);
        } else {
          if (targetUrl.startsWith('blob:')) {
            setErrors((prev) => ({
              ...prev,
              attachments: "Could not download image from cross-origin blob URL. Please copy (Ctrl+C) the image from WhatsApp and paste (Ctrl+V) it onto this upload area, or download and upload it."
            }));
          } else {
            setErrors((prev) => ({
              ...prev,
              attachments: "Failed to download image from the dropped URL. Try copy-pasting the image (Ctrl+C then Ctrl+V) or downloading it first."
            }));
          }
        }
      }
    }

    if (files.length > 0) {
      addFiles(files);
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };



  const removeAttachment = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, index) => index !== indexToRemove),
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const totalAttachmentSize = form.attachments.reduce((acc, file) => acc + (file.size || 0), 0);

  const validate = () => {
    const newErrors = {};

    // Admin-specific fields
    if (isAdmin && !form.client && !isEditMode) {
      newErrors.client = 'Client is required';
    }
    if (isAdmin && !form.customer && !isEditMode) {
      newErrors.customer = 'Customer is required';
    }
    // Common required fields
    const effectiveModule = form.module || module_name || ticket?.module_name || ticket?.module_id;
    if (!effectiveModule) {
      newErrors.module = 'Module is required';
    }
    if (!form.priority) {
      newErrors.priority = 'Priority is required';
    }
    if (!form.status) {
      newErrors.status = 'Status is required';
    }
    if (!form.title.trim() && !isEditMode) {
      newErrors.title = 'Title is required';
    }

    // For internal tickets (ENOVA) or Edit Mode or Code Review Request or Production Move Request, Assigned To is required
    const isInternal = String(form.client) === '108';
    if (
      !(isDeployer && isTicketInProdMove) &&
      !(!isEditMode && isClientRole) &&
      userInfo?.role_id != '2' && showAssignee && (!form.assigned_to || form.assigned_to.length === 0) && (isEditMode || isInternal || [14, 17].includes(Number(form.status)))
    ) {
      newErrors.assigned_to = Number(form.status) === 14 ? 'Code Reviewer is required' : Number(form.status) === 17 ? 'Production Engineer is required' : 'Assigned to is required';
    }

    // if (!form.issue_type && isEditMode && (userInfo.role_id == '1' || userInfo.role_id == '3')) {
    //   newErrors.issue_type = 'Issue Type is required';
    // }
    const isIssueTypeEditable = userInfo?.role_id === '1' || userInfo?.role_id === '3' || [25, 44].includes(Number(userInfo?.designation_id));
    if (!form.issue_type && !(!isEditMode && isClientRole) && userInfo?.role_id != '2' && !isClientAdmin && isIssueTypeEditable) {
      newErrors.issue_type = 'Issue Type is required';
    }

    if (form.issue_type === 'Enhancement' && !form.enhancement_type) {
      newErrors.enhancement_type = 'Enhancement Type is required';
    }

    if (form.issue_type === 'Enhancement' && form.enhancement_type === 'Billable' && !form.enhancement_hours) {
      newErrors.enhancement_hours = 'Enhancement Hours are required';
    }

    // Clean and validate rich-text description
    const plainDescription = form.description.replace(/<(.|\n)*?>/g, '').trim();
    if (!plainDescription) {
      newErrors.description = 'Description is required';
    }

    // Recovery & Restart (2 fields) — mandatory ONLY for developers moving a Bug to Testing
    const rrRoleId = Number(userInfo?.role_id ?? currentUser?.role_id ?? 0);
    const rrUserId = Number(userInfo?.user_id ?? currentUser?.user_id ?? 0);
    const rrDesig = String(userInfo?.designation_name || currentUser?.designation_name || '').toLowerCase();
    const rrRoleName = String(userInfo?.role_name || currentUser?.role_name || '').toLowerCase();
    const rrIsTester = rrRoleId === 5 || [26, 42].includes(Number(userInfo?.designation_id ?? currentUser?.designation_id ?? 0)) || rrUserId === 101 || rrDesig.includes('test') || rrDesig.includes('qa') || rrRoleName.includes('test') || rrRoleName.includes('qa');
    const isDeveloper = ![1, 2, 3].includes(rrRoleId) && !rrIsTester;
    const isTestingBug = isEditMode && String(form.status) === '8' && form.issue_type === 'Bug' && isDeveloper;
    if (isTestingBug) {
      if (!form.recovery_steps.trim()) {
        newErrors.recovery_steps = 'Problem Recovery is required.';
      }
      if (!form.restart_steps.trim()) {
        newErrors.restart_steps = 'Restart Procedure is required.';
      }
    }

    setErrors(newErrors);
    console.log(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async (e) => {
    e?.preventDefault(); // Allow calling manually without event

    // console.log("VALIDATE START");
    const isValid = validate();
    // console.log("VALIDATE RESULT", isValid);
    if (!isValid) return;

    // console.log("AFTER VALIDATE, before formData ");
    setSubmitting(true);

    try {
      const formData = new FormData();

      // Append fields
      formData.append('ticketTitle', form.title);
      formData.append('priority', form.priority);
      formData.append('status', form.status);
      formData.append('description', form.description);
      formData.append('module', form.module || module_name || ticket?.module_name || ticket?.module_id || '');
      formData.append('recovery_steps', form.recovery_steps || '');
      formData.append('restart_steps', form.restart_steps || '');

      // Handle assigned_to
      const isInternal = String(form.client) === '108';
      if (Number(form.status) === 17) {
        const prodIds = Array.isArray(form.assigned_to) && form.assigned_to.length > 0
          ? form.assigned_to
          : (productionEngineerUsers.length > 0 ? productionEngineerUsers.map(u => String(u.user_id)) : ['113', '169']);
        prodIds.forEach(id => formData.append('assigned_to[]', id));
        formData.append('assigned_to', prodIds[0]);
      } else {
        const assigneeValue = Array.isArray(form.assigned_to) ? form.assigned_to[0] : form.assigned_to;
        formData.append('assigned_to', assigneeValue || '');
      }

      // Handle CC
      if (isInternal && Array.isArray(form.internal_cc)) {
        form.internal_cc.forEach((ccId) => {
          formData.append('internal_cc[]', ccId);
        });
      }

      // Identity
      // formData.append('user_id', userInfo.user_id);
      // formData.append('role_id', userInfo.role_id);
      formData.append('designation_id', userInfo.designation_id);

      if (isAdmin) {
        formData.append('cust_key', form.client);
        formData.append('child_id', form.customer);
      } else {
        formData.append('cust_key', userInfo.cust_key);
        formData.append('child_id', userInfo.child_id);
      }

      const clientIdToSend =
        userInfo.role_id === '2' ? userInfo.cust_key : form.client;

      if (isAdmin) {
        formData.append('clientId', clientIdToSend);
        formData.append('customerId', userInfo.name);
      } else {
        formData.append('clientId', userInfo?.cust_key);
        formData.append('clientName', form.customer);
      }
      formData.append('issue_type', form.issue_type || ticket?.issue_type || '');


      if (form.issue_type === 'Enhancement') {
        formData.append('enhancement_type', form.enhancement_type);
        if (form.enhancement_type === 'Billable') {
          formData.append('enhancement_hours', form.enhancement_hours);
        }
      }
      formData.append('customer_name', ticket?.customer_name);

      // for (let [key, value] of formData.entries()) {
      //   console.log(`${key}:`, value);
      // }
      // return;

      for (let i = 0; i < form.attachments.length; i++) {
        formData.append('files[]', form.attachments[i]);
      }

      // Determine create or update
      const endpoint = selectedTicketId ? `/ticket/${selectedTicketId}` : '/ticket';
      const method = selectedTicketId ? 'post' : 'post';

      const res = await api({
        method,
        url: endpoint,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.status) {
        try {
          window.dispatchEvent(new CustomEvent('ticket-updated', { detail: { ticketId: selectedTicketId } }));
        } catch (evtErr) {
          console.error('ticket-updated dispatch error:', evtErr);
        }
        try {
          onSuccess?.();
        } catch (callbackErr) {
          console.error('onSuccess callback error:', callbackErr);
        }
        try {
          if (hasNext && onNext) {
            onNext();
          } else {
            onClose?.();
          }
        } catch (closeErr) {
          console.error('onClose / onNext error:', closeErr);
        }
      } else {
        toast.error(res.data.message || 'Ticket submission failed');
      }

    } catch (err) {
      console.error('Ticket submission failed:', err);
      const errMsg = err.response?.data?.message || err.response?.data?.messages?.error || err.message || 'An error occurred while submitting the ticket.';
      toast.error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
    } finally {
      setSubmitting(false);
    }
  };


  const renderError = (field) =>
    errors[field] ? <p className="text-xs text-red-500 mt-1">{errors[field]}</p> : null;

  const restrictedStatusMap = {
    'New': ['6'],
    'IN process': ['1', '6'],
    'Pending verification & closure': ['1'],
    'Closed': ['1', '3', '4'],
    'Reopen': ['1']
  };

  const filteredClients = clientList?.filter(client => {
    const roleId = String(userInfo?.role_id);
    const designationId = Number(userInfo?.designation_id);
    const userId = Number(userInfo?.user_id);

    // Admin (role_id 1), CA (role_id 3), Project Manager (44), Designation 63, and Mari Selvam (42 / 101) see all clients
    if (roleId === '1' || roleId === '3' || designationId === 44 || designationId === 63 || designationId === 42 || userId === 101) {
      return true;
    }
    // All other employees see only ENOVA (cust_key 108)
    return String(client.cust_key) === '108';
  });

  useEffect(() => {
    if (!isEditMode && filteredClients?.length === 1 && !form.client) {
      const defaultClient = filteredClients[0];

      // Update form state
      setForm(prev => ({
        ...prev,
        client: defaultClient.cust_key,
        customer: '',
        module: ''
      }));

      // Manually trigger customer fetch logic
      (async () => {
        setModuleOptions([]);
        setCustomerOptions([]);
        setIsCustomerLoading(true);
        try {
          const res = await api.get('/ticket/get-customers', {
            params: { cust_key: defaultClient.cust_key },
          });
          if (res.data.status) {
            setCustomerOptions(res.data.data);
          }
        } catch (error) {
          console.error('Failed to fetch customers:', error);
        } finally {
          setIsCustomerLoading(false);
        }
      })();
    }
  }, [filteredClients, form.client]);


  useEffect(() => {
    if (selectedClientId) {
      handleChange({
        target: { name: "client", value: selectedClientId },
      });
    }
  }, [selectedClientId]);
  const clientOptions = useMemo(() => {
    return (filteredClients || []).map(client => ({
      value: client.cust_key,
      label: client.short_code?.trim() ? client.short_code : client.customer_name
    }));
  }, [filteredClients]);

  const selectedClientOption = useMemo(() => {
    return clientOptions.find(opt => String(opt.value) === String(form.client)) || null;
  }, [clientOptions, form.client]);

  const customerOptionsMapped = useMemo(() => {
    return (customerOptions || []).map(cust => ({
      value: cust.cust_key,
      label: cust.customer_name
    }));
  }, [customerOptions]);

  const selectedCustomerOption = useMemo(() => {
    return customerOptionsMapped.find(opt => String(opt.value) === String(form.customer)) || null;
  }, [customerOptionsMapped, form.customer]);

  const selectedModuleOption = useMemo(() => {
    const val = form.module || module_name;
    return moduleOptions.find(opt => String(opt.value) === String(val)) || (val ? { value: val, label: module_name || val } : null);
  }, [moduleOptions, form.module, module_name]);

  const selectedPriorityOption = useMemo(() => {
    return priorityOptions.find(opt => String(opt.value) === String(form.priority)) || null;
  }, [priorityOptions, form.priority]);

  const statusOptionsFiltered = useMemo(() => {
    if (!selectedTicketId) {
      return [{ value: '1', label: 'New' }];
    }

    // In edit mode, determine whether ticket is internal strictly from the ticket itself (client_key 108 or ENOVA in name).
    // For new tickets, fall back to form.client or selectedClientId.
    const isInternal = isEditMode
      ? (String(ticket?.client_key || '') === '108' ||
         String(ticket?.client || '') === '108' ||
         String(ticket?.client_name || '').toUpperCase().includes('ENOVA'))
      : (String(ticket?.client_key || '') === '108' ||
         String(form?.client || '') === '108' ||
         String(selectedClientId || '') === '108' ||
         String(ticket?.client_name || '').toUpperCase().includes('ENOVA') ||
         String(form?.client || '').toUpperCase().includes('ENOVA'));

    const verificationStatusId = isInternal ? 3 : 12;

    const roleId = Number(userInfo?.role_id ?? currentUser?.role_id ?? currentUser?.user_role_id ?? 0);
    const isSystemAdmin = (roleId === 1 || isAdmin || isAdminUser);

    // For the admin alone, for all tickets, show all status options (excluding 'New' 1 when editing)
    // strictly enforce verification status: 3 for internal, and allow both 3 & 12 for external
    if (isSystemAdmin) {
      return statusOptions.filter((opt) => {
        const optValue = Number(opt.value);
        if (optValue === 1) return false;
        if (isInternal && optValue === 12) return false;
        return true;
      });
    }

    // For Client Admin (Role 17) alone, strictly allow ONLY:
    // 1. IN process (4)
    // 2. Pending verification & closure (3)
    // 3. Pending Customer Verification & Confirmation (12)
    // 4. On hold (13)
    const isClientAdmin = roleId === 17 || String(userInfo?.role_name || currentUser?.role_name || '').toLowerCase().includes('client admin');
    if (isClientAdmin) {
      const userId = Number(userInfo?.user_id ?? currentUser?.user_id ?? currentUser?.id ?? 0);
      const isCreator = String(ticket?.created_by || ticket?.created_by_user_id) === String(userId);

      return statusOptions.filter((opt) => {
        const optValue = Number(opt.value);
        if (Number(selectedStatusId) === 5) {
          // If ticket is already closed, allow Reopen (6) or IN Process (4)
          return [4, 6].includes(optValue);
        }
        // If ticket was created by Client Admin:
        // The 2 pending statuses (3, 12) are not needed. Show ONLY IN process (4), Closed (5), and On hold (13).
        if (isCreator) {
          return [4, 5, 13].includes(optValue);
        }
        // If created by others (e.g. client):
        // Show IN process (4), the 2 pending statuses (3, 12), and On hold (13)
        return [4, 3, 12, 13].includes(optValue);
      });
    }

    return statusOptions.filter((opt) => {
      const userId = Number(userInfo?.user_id ?? currentUser?.user_id ?? currentUser?.id ?? 0);
      const currentStatusId = Number(selectedStatusId);
      const optValue = Number(opt.value);
      const designationId = Number(userInfo?.designation_id ?? currentUser?.designation_id ?? 0);
      const userDesig = String(userInfo?.designation_name || currentUser?.designation_name || '').toLowerCase();
      const userRoleName = String(userInfo?.role_name || currentUser?.role_name || '').toLowerCase();

      // Check if user is a Tester (by role, designation, ID 101, or title containing QA/Test)
      const isTester = (
        roleId === 5 ||
        designationId === 42 ||
        designationId === 26 ||
        userId === 101 ||
        userDesig.includes('test') ||
        userDesig.includes('qa') ||
        userRoleName.includes('test') ||
        userRoleName.includes('qa')
      );

      const isCreator = String(ticket?.created_by || ticket?.created_by_user_id) === String(userId);
      const isVerticalHead = (
        [25, 26, 42, 43, 44, 63].includes(designationId) ||
        userDesig.includes('vertical head') ||
        userDesig.includes('project manager') ||
        userRoleName.includes('vertical head') ||
        userRoleName.includes('project manager')
      );
      const isClientAdvocate = (
        roleId === 3 ||
        userRoleName.includes('client advocate') ||
        userDesig.includes('client advocate')
      );
      const isAdvocateOrHeadOrAdmin = roleId === 1 || isClientAdvocate || isVerticalHead;
      const isCodeReviewer = hasRole(userInfo, 8) || hasRole(currentUser, 8) || roleId === 1;
      const isDeployer = hasRole(userInfo, 6) || hasRole(currentUser, 6) || roleId === 1;

      // Cannot select New (1) when editing an existing ticket
      if (optValue === 1) return false;

      // Internal tickets: ONLY Pending verification & closure (3), NEVER Pending Customer Verification (12)
      if (isInternal && optValue === 12) return false;
      // External tickets: Status 3 is shown to Vertical Heads, Client Advocates (Role 3), Admin (Role 1), and Client Admin (Role 17). Status 12 is shown to Vertical Heads, Client Advocates, Admin
      if (!isInternal) {
        if (optValue === 3 && !isAdvocateOrHeadOrAdmin && roleId !== 17 && !isClientAdmin) return false;
        if (optValue === 12 && !isAdvocateOrHeadOrAdmin) return false;
      }

      // Code Review Request (14) is only available AFTER testing is complete (Testing Approved = 9)
      // and is NEVER permitted for ANY Tester.
      if (optValue === 14 && (!hasPassedTesting || isTester)) return false;

      // Production Move Request (17) is only available AFTER Code Review is Approved (15)
      // and is NEVER permitted for ANY Tester.
      if (optValue === 17 && (!hasPassedCodeReview || isTester)) return false;

      // "Pending verification & closure" (3) is available when ticket is not Closed (5)
      // For internal: available to all users. For external: available to Vertical Heads, Client Advocates, Admin, and Client Admin (17)
      if (optValue === 3) {
        if (isInternal) {
          return currentStatusId !== 5;
        }
        return (isAdvocateOrHeadOrAdmin || roleId === 17 || isClientAdmin) && currentStatusId !== 5;
      }

      // For external tickets, "Pending Customer Verification & Confirmation" (12) is available ONLY to Vertical Heads, Client Advocates (Role 3), and Admin (Role 1)
      if (!isInternal && optValue === 12) {
        return isAdvocateOrHeadOrAdmin && currentStatusId !== 5;
      }

      // Allow "On Hold" (13) for all employees (non-clients) at any stage
      if (optValue === 13) {
        return ![2, 17].includes(roleId) && !isClientAdmin;
      }

      // Reopen (6) is only permitted when ticket is currently Closed (5)
      if (currentStatusId !== 5 && optValue === 6) {
        return false;
      }

      // 1. Client Admin (Role 17) Workflow - Client Admin has access to Pending verification & closure (3), IN Process (4), Close (5), Reopen (6)
      if (roleId === 17 || isClientAdmin) {
        if ([4, 7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20].includes(currentStatusId)) return [3, 4, 5].includes(optValue);
        if (currentStatusId === 3) return [3, 4, 5].includes(optValue);
        if (currentStatusId === 5) return [5, 6].includes(optValue);
        if (currentStatusId === 6) return [3, 4, 6].includes(optValue);
        if (currentStatusId === 12) return [3, 4, 5].includes(optValue);
        return [3, 4, 5].includes(optValue);
      }

      // 2. Customer (Role 2) Workflow - Client always has access to Close / Reopen external tickets based on status
      if (roleId === 2) {
        if ([4, 7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20].includes(currentStatusId)) return [4, 5].includes(optValue);
        if (currentStatusId === 3) return [4, 5].includes(optValue);
        if (currentStatusId === 5) return [5, 6].includes(optValue);
        if (currentStatusId === 6) return [4, 6].includes(optValue);
        if (currentStatusId === 12) return [4, 5].includes(optValue);
        return ![6, 8, 9, 10, 12, 14, 15, 16, 17, 18, 19, 20].includes(optValue);
      }

      // Closing (5) for internal tickets requires ticket creator or Admin; for external tickets allowed for creator, admin, or client
      if (optValue === 5) {
        return isCreator || roleId === 1 || [2, 17].includes(roleId);
      }

      // CI/CD Team (Role 6, Deployer) Workflow:
      // Deployers only handle deployment decisions when the ticket is in Production Move Request (17).
      if (isDeployer && roleId !== 1 && currentStatusId === 17) {
        return [18, 20].includes(optValue);
      }

      // 2. Testing Decision Statuses (9: Testing Approved, 10: Testing Rejected)
      // Strictly ONLY for Testers in Status 8, or Client Advocates / Vertical Heads / Admin
      if ([9, 10].includes(optValue)) {
        if (isTester && [8, 9, 10].includes(currentStatusId)) return true;
        if (isAdvocateOrHeadOrAdmin && [8, 9, 10].includes(currentStatusId)) return true;
        return false;
      }

      // 3. Code Review Decision Statuses (15: Code Review Approved, 16: Code Review Rejected)
      // Strictly ONLY for Code Reviewers (Role 8 / Admin)
      if ([15, 16].includes(optValue)) {
        return isCodeReviewer && currentStatusId === 14;
      }

      // 4. Production Decision Statuses (18: Production Moved, 20: Production Rejected)
      // Strictly ONLY for CI/CD Team (Role 6 / Admin) when in Production Move Request (17)
      if ([18, 20].includes(optValue)) {
        return isDeployer && currentStatusId === 17;
      }

      // 5. Status 14: Code Review Request
      if (currentStatusId === 14) {
        if (isCodeReviewer || isVerticalHead || roleId === 1) {
          return [14, 15, 16, 4, 13].includes(optValue);
        }
        return [14, 4, 13].includes(optValue);
      }

      // 6. Status 15: Code Review Approved
      // Strictly ONLY the developer who worked on this ticket and requested testing / code review can request Production Move (17) or re-request Code Review (14)
      // Vertical Heads, Managers, and other roles do NOT have access unless they are the actual developer of this ticket
      if (currentStatusId === 15) {
        const devRequesterId = ticket?.code_review_requester_id || ticket?.original_developer_id;
        const hasRequestedReviewOrTesting = Array.isArray(ticket?.comments) && ticket.comments.some(c => 
          [8, 14].includes(Number(c?.status_id)) && String(c?.updated_by) === String(userInfo?.user_id)
        );
        const isDeveloperWhoWorked = (devRequesterId && String(devRequesterId) === String(userInfo?.user_id)) || hasRequestedReviewOrTesting;

        if (isDeveloperWhoWorked) {
          return [15, 14, 17, 4, 13].includes(optValue);
        }

        return [15, 4, 13].includes(optValue);
      }

      // 7. Status 16: Code Review Rejected
      // Developer makes corrections, can submit again for Code Review Request (14)
      if (currentStatusId === 16) {
        return (isCodeReviewer ? [16, 14, 4, 8, 13] : [14, 4, 8, 13]).includes(optValue);
      }

      // 8. Status 17: Production Move Request (assigned to CICD Team)
      // CI/CD Engineer marks as Production Moved (18) or Production Rejected (20) ONLY
      if (currentStatusId === 17) {
        if (isDeployer) {
          return [18, 20].includes(optValue);
        }
        if (isVerticalHead || roleId === 1) {
          return [17, 18, 20, 4, 13].includes(optValue);
        }
        return [17, 4, 13].includes(optValue);
      }

      // 9. Status 20: Production Rejected
      // Developer makes corrections, can submit again for Production Move Request (17), Code Review Request (14), IN process (4)
      if (currentStatusId === 20) {
        return [20, 17, 14, 4, 8, 13].includes(optValue);
      }

      // 10. Status 18: Production Moved
      // Developer tests on production, then moves to Production Tested (19)
      if (currentStatusId === 18) {
        return (isDeployer ? [18, 19, 4, 13] : [19, 4, 13]).includes(optValue);
      }

      // 11. Status 19: Production Tested
      // Move to Pending verification & closure (3) for internal, or Pending Customer Verification (12) / Pending verification & closure (3) for external, or Closed (5)
      if (currentStatusId === 19) {
        const allowed = [19, 4, 13, 5];
        if (isInternal) {
          allowed.push(3);
        } else {
          if (isAdvocateOrHeadOrAdmin) allowed.push(12, 3);
        }
        return allowed.includes(optValue);
      }

      // 12. Status 8: Testing
      if (currentStatusId === 8) {
        if (roleId === 1 || isVerticalHead || isClientAdvocate) {
          // Code Review Request (14) and Production Move Request (17) should only
          // appear AFTER testing is complete (Testing Approved = 9), not during Testing.
          const allowed = [4, 8, 9, 10, 13];
          if (isInternal) {
            allowed.push(3);
          } else {
            allowed.push(12, 3);
          }
          return allowed.includes(optValue);
        }
        if (isTester) {
          return [8, 9, 10, 4, 13].includes(optValue);
        }
        return [8, 4, 13].includes(optValue);
      }

      // 13. Status 5: Closed
      if (currentStatusId === 5) {
        return [5, 6].includes(optValue);
      }

      // 14. General handling when in other statuses (e.g. New 1, IN process 4, Testing Rejected 10, etc.)
      if (isTester) {
        return [4, 8, 13, verificationStatusId, currentStatusId].includes(optValue);
      }

      if (roleId === 1) return true;

      if (isVerticalHead) {
        const allowed = [4, 8, 14, 17, 13, currentStatusId, 3];
        if (!isInternal) allowed.push(12);
        return allowed.includes(optValue);
      }

      // Client Advocate: exclude Code Review Request (14) and Production Move Request (17)
      if (isClientAdvocate) {
        const allowed = [4, 8, 13, currentStatusId, 3];
        if (!isInternal) allowed.push(12);
        return allowed.includes(optValue);
      }

      // Developers / General Staff:
      // Can select IN process (4), Testing (8), Code Review Request (14), On Hold (13), and for internal: Pending verification & closure (3)
      if (isInternal) {
        return [4, 8, 14, 13, 3, currentStatusId].includes(optValue);
      }
      const safeCurrentStatusId = [3, 12].includes(currentStatusId) ? null : currentStatusId;
      return [4, 8, 14, 13, safeCurrentStatusId].filter(Boolean).includes(optValue);
    });
  }, [selectedTicketId, statusOptions, userInfo, currentUser, selectedStatusId, ticket, form.client, isEditMode, hasPassedTesting, hasPassedCodeReview, isClientAdmin]);

  const selectedStatusOption = useMemo(() => {
    return statusOptionsFiltered.find(opt => String(opt.value) === String(form.status)) || null;
  }, [statusOptionsFiltered, form.status]);

  const issueTypeOptions = [
    { value: 'Bug', label: 'Bug' },
    { value: 'Enhancement', label: 'Enhancement' },
    { value: 'Others', label: 'Others' }
  ];

  const selectedIssueTypeOption = useMemo(() => {
    return issueTypeOptions.find(opt => opt.value === form.issue_type) || null;
  }, [form.issue_type]);

  return (
    <div className="bg-white max-w-6xl mx-auto mb-8">
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
          {isAdmin && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 ">Client <span className="text-red-500">*</span></label>
                <Select
                  name="client"
                  options={clientOptions}
                  value={selectedClientOption}
                  placeholder="Select Client"
                  isSearchable={true}
                  isClearable={true}
                  className="react-select-container text-sm"
                  classNamePrefix="react-select"
                  onChange={(selectedOption) => {
                    handleChange({
                      target: {
                        name: 'client',
                        value: selectedOption ? selectedOption.value : ''
                      }
                    });
                  }}
                />
                {renderError('client')}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center ">
                  Customer Name <span className="text-red-500 ml-1">*</span>
                  {isCustomerLoading && (
                    <span className="animate-spin inline-block w-4 h-4 border-[2px] border-current border-r-transparent rounded-full align-middle ml-2 "></span>
                  )}
                </label>
                <Select
                  name="customer"
                  options={customerOptionsMapped}
                  value={selectedCustomerOption}
                  placeholder="Select Customer"
                  isSearchable={true}
                  isClearable={true}
                  className="react-select-container text-sm"
                  classNamePrefix="react-select"
                  onChange={(selectedOption) => {
                    handleChange({
                      target: {
                        name: 'customer',
                        value: selectedOption ? selectedOption.value : ''
                      }
                    });
                  }}
                />
                {renderError('customer')}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1 flex items-center ">
              Module <span className="text-red-500 ml-1">*</span>
              {isModuleLoading && (
                <span className="animate-spin inline-block w-4 h-4 border-[2px] border-current border-r-transparent rounded-full align-middle ml-2 text-blue-500"></span>
              )}
            </label>
            <Select
              name="module"
              options={moduleOptions}
              value={selectedModuleOption}
              placeholder="Select Module"
              isSearchable={true}
              isClearable={true}
              isDisabled={selectedTicketChildId !== '' && module_name !== ''}
              className="react-select-container text-sm"
              classNamePrefix="react-select"
              onChange={(selectedOption) => {
                handleChange({
                  target: {
                    name: 'module',
                    value: selectedOption ? selectedOption.value : ''
                  }
                });
              }}
            />
            {renderError('module')}
          </div>

        </div>
        <div className={`grid ${(!isEditMode && isClientRole) ? 'grid-cols-2 md:grid-cols-2' : 'grid-cols-3 md:grid-cols-3'} gap-2`}>

          <div>
            <label className="block text-sm font-medium mb-1 ">Priority <span className="text-red-500">*</span></label>
            <Select
              name="priority"
              options={priorityOptions}
              value={selectedPriorityOption}
              placeholder="Select Priority"
              isSearchable={true}
              isClearable={true}
              className="react-select-container text-sm"
              classNamePrefix="react-select"
              onChange={(selectedOption) => {
                handleChange({
                  target: {
                    name: 'priority',
                    value: selectedOption ? selectedOption.value : ''
                  }
                });
              }}
            />
            {renderError('priority')}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 ">Status <span className="text-red-500">*</span></label>
            <Select
              name="status"
              options={statusOptionsFiltered}
              value={selectedStatusOption}
              placeholder="Select Status"
              isSearchable={true}
              isClearable={true}
              className="react-select-container text-sm"
              classNamePrefix="react-select"
              onChange={(selectedOption) => {
                handleChange({
                  target: {
                    name: 'status',
                    value: selectedOption ? selectedOption.value : ''
                  }
                });
              }}
            />
            {renderError('status')}
          </div>

          {(isEditMode ? (showAssignee && (userInfo?.role_id != '2' || isClientAdmin)) : (String(form.client) === '108' && !isClientRole)) && form.status != 3 && (
            <div>
              <label className="block text-sm font-medium mb-1 ">
                {Number(form.status) === 14 ? 'Code Reviewer' : (Number(form.status) === 17 ? 'CI/CD Team (Assigned to Both)' : (isClientAdmin ? 'Forward / Assign To' : 'Assigned To'))} <span className="text-red-500">*</span>
              </label>
              {Number(form.status) === 17 ? (
                <div className="h-[38px] flex items-center bg-gray-50 border border-gray-300 rounded px-3 text-sm text-gray-800 font-medium truncate shadow-sm">
                  <span className="truncate" title={productionEngineerNamesLabel}>
                    {productionEngineerNamesLabel} <span className="text-xs text-gray-500 font-normal">(CI/CD Team)</span>
                  </span>
                </div>
              ) : (
                <Select
                  ref={assigneeRef}
                  name="assigned_to"
                  options={
                    Number(form.status) === 14
                      ? codeReviewerOptions
                      : assigneeOptions
                  }
                  isDisabled={false}
                  isMulti={false}
                  value={
                    Number(form.status) === 14
                      ? (codeReviewerOptions.find((opt) => String(opt.value) === String(Array.isArray(form.assigned_to) ? form.assigned_to[0] : form.assigned_to)) || null)
                      : (assigneeOptions.find((opt) => String(opt.value) === String(Array.isArray(form.assigned_to) ? form.assigned_to[0] : form.assigned_to)) || null)
                  }
                  placeholder={
                    Number(form.status) === 14
                      ? "Select Code Reviewer"
                      : "Select Assignee"
                  }
                  onChange={(selected) => {
                    setForm({ ...form, assigned_to: selected ? [selected.value] : [] });
                  }}
                  formatOptionLabel={(opt) => {
                    if (opt && opt.pendingCount !== undefined) {
                      return (
                        <span>
                          <span>{opt.baseLabel || opt.label}</span>{" "}
                          <span className="text-[11px] font-semibold text-red-600 inline-block ml-1">
                            ({opt.pendingCount} Pending)
                          </span>
                        </span>
                      );
                    }
                    return opt?.label;
                  }}
                  isClearable={true}
                  className="react-select-container text-sm"
                  classNamePrefix="react-select"
                />
              )}
              {renderError('assigned_to')}
            </div>
          )}

          {(!(!isEditMode && isClientRole)) && (String(form.client) === '108' || String(form.client).toUpperCase() === 'INTERNAL' || (isEditMode && (ticket?.client_name === '108' || String(ticket?.client_name).toUpperCase() === 'INTERNAL' || String(ticket?.client_name).toUpperCase().includes('ENOVA')))) && (
            <div className=''>
              <label className="block text-sm font-medium mb-1 ">
                CC (optional)
              </label>
              {isEditMode && (!form.internal_cc || form.internal_cc.length === 0) && (userInfo?.role_id !== '1' && userInfo?.role_id !== '3') ? (
                <div className="text-sm text-gray-500 bg-gray-50 border rounded px-3 py-2 italic">None</div>
              ) : (
                <Select
                  name="internal_cc"
                  options={verticalHeadsOptions}
                  isMulti
                  value={verticalHeadsOptions.filter(opt => (form.internal_cc || []).map(String).includes(String(opt.value)))}
                  placeholder="Select users to notify"
                  onChange={(selected) => setForm({ ...form, internal_cc: selected ? selected.map(s => s.value) : [] })}
                  isClearable
                  isDisabled={isEditMode && (userInfo?.role_id === '2' || (ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id)))}
                  className="text-sm"
                  classNamePrefix="react-select"
                  menuPlacement="auto"
                />
              )}
            </div>
          )}

          {
            !isClientAdmin && (
              isEditMode
                ? (userInfo?.role_id != '2' || form.issue_type !== '')
                : !isClientRole
            ) && (
              <div className=''>
                <label className="block text-sm font-medium mb-1 ">
                  Issue Type <span className="text-red-500">*</span>
                </label>
                <Select
                  name="issue_type"
                  options={issueTypeOptions}
                  value={selectedIssueTypeOption}
                  placeholder="Select Issue Type"
                  isSearchable={true}
                  isClearable={true}
                  isDisabled={
                    userInfo?.role_id === '2' ||
                    (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id) && ![25, 44].includes(Number(userInfo?.designation_id)))
                  }
                  className="react-select-container text-sm"
                  classNamePrefix="react-select"
                  onChange={(selectedOption) => {
                    handleChange({
                      target: {
                        name: 'issue_type',
                        value: selectedOption ? selectedOption.value : ''
                      }
                    });
                  }}
                />
                {renderError('issue_type')}
              </div>
            )
          }

          {isSecondCodeReviewer && (
            <div className="flex flex-col justify-end">
              <div className="p-2 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-900 flex items-center gap-1.5 shadow-sm leading-tight">
                <span className="text-amber-600 font-bold shrink-0 text-xs">⚠️</span>
                <span>
                  <strong>Note:</strong> Discuss with last reviewer{lastReviewerName ? ` (${lastReviewerName})` : ''} who {lastReviewAction} the code.
                </span>
              </div>
            </div>
          )}

          {form.issue_type === 'Enhancement' && (
            <div>
              <label className="block text-sm font-medium mb-1 ">
                Enhancement Type <span className="text-red-500">*</span>
              </label>
              <select
                name="enhancement_type"
                value={form.enhancement_type}
                onChange={handleChange}
                disabled={
                  userInfo?.role_id === '2' ||
                  (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id))
                }
                className={`block w-full rounded px-3 py-2 text-sm   appearance-none ${errors.issue_type ? 'border-red-500' : 'border-gray-300'
                  } border bg-white ${userInfo?.role_id === '2' ||
                    (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id))
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                  }`}
              >
                <option value="">Select Enhancement Type</option>
                <option value="Billable">Billable</option>
                <option value="Non-Billable">Non-Billable</option>
              </select>
              {renderError('enhancement_type')}
            </div>
          )}

          {/* || form.enhancement_type === 'Non-Billable' */}
          {(form.enhancement_type === 'Billable') &&
            (
              !isEditMode || // show always in create mode
              userInfo?.role_id === '2' ||
              ticket?.client_name === 'INTERNAL' ||
              (ticket?.client_name !== 'INTERNAL' && ['1', '3'].includes(userInfo?.role_id))
            ) && (
              <div>
                <label className="block text-sm font-medium mb-1 ">
                  Enhancement Hours <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="enhancement_hours"
                  value={form.enhancement_hours}
                  onChange={handleChange} // Always allow typing
                  onBlur={(e) => {
                    const value = e.target.value.trim();

                    // allow numbers below 1000, up to 2 decimals
                    const isValidFormat = /^(?:0|[1-9]\d{0,2})(?:\.\d{1,2})?$/.test(value);

                    if (!value) {
                      setErrors((prev) => ({ ...prev, enhancement_hours: null }));
                      return;
                    }

                    if (!isValidFormat) {
                      setErrors((prev) => ({ ...prev, enhancement_hours: 'Max allowed: below 730 hours' }));
                      return;
                    }

                    const number = Number(value);

                    if (number >= 730) {
                      setErrors((prev) => ({ ...prev, enhancement_hours: 'Max allowed: below 730 hours' }));
                    } else {
                      setErrors((prev) => ({ ...prev, enhancement_hours: null }));
                    }
                  }}
                  placeholder="Enter hours (Max 730 hours)"
                  className={`block w-full rounded px-3 py-2 text-sm appearance-none ${errors.enhancement_hours ? 'border-red-500' : 'border-gray-300'
                    } border bg-white ${userInfo?.role_id === '2' ||
                      (isEditMode && ticket?.client_name !== 'INTERNAL' && !['1', '3'].includes(userInfo?.role_id))
                      ? 'bg-gray-100 cursor-not-allowed'
                      : ''
                    }`}
                />

                {renderError('enhancement_hours')}
              </div>
            )
          }



        </div>


        {!selectedTicketId &&
          <div>
            <label className="block text-sm font-medium mb-1 ">
              Ticket Title <span className="text-red-500">*</span>
            </label>

            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              maxLength={150}
              placeholder="e.g. Unable to upload file"
              className={`block w-full rounded  text-sm   appearance-none ${errors.title ? 'border-red-500' : 'border-gray-300'
                } border`}
            />

            <div className="text-xs text-gray-500 mt-1 text-right">
              {form.title.length}/150 characters
            </div>

            {renderError('title')}
          </div>

        }

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <ReactQuill
                ref={descriptionRef}
                theme="snow"
                value={form.description}
                onChange={(val) => {
                  setForm((prev) => ({ ...prev, description: val }));
                  if (descriptionRef.current) {
                    const quill = descriptionRef.current.getEditor();
                    handleAutoLink(quill);
                  }
                }}
                placeholder="Briefly describe the issue or request..."
                modules={quillModules}
                className="custom-quill rounded-md border border-gray-300 bg-white"
              />
              <style>{`
                .custom-quill .ql-container {
                  min-height: 120px !important;
                  max-height: 400px;
                  overflow: hidden !important;
                }
                .custom-quill .ql-editor {
                  min-height: 120px !important;
                  max-height: 400px;
                  overflow-y: auto !important;
                }
                .custom-quill .ql-editor a {
                  color: #2563eb !important;
                  text-decoration: underline !important;
                }
              `}</style>
            </div>
            {renderError('description')}
            {/* onKeyDown={handleKeyDown} */}

          </div>

          <div>
            <label className="block text-sm font-medium mb-1 ">Attachments</label>

            {/* Upload Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onPaste={handlePaste}
              className={`relative border border-dashed rounded p-1.5 text-center transition-all ${isDragging ? 'border-blue-500 bg-blue-50/50 scale-[1.01]' : 'border-gray-300'
                }`}
            >
              <input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.pdf,.docx,.xls,.xlsx,.csv,.txt,.zip"
                onChange={handleFileChange}
                className="absolute inset-0 opacity-0 cursor-pointer"
                id="fileUpload"
              />
              <div className="flex items-center justify-center">
                <CloudArrowUpIcon className="w-6 h-6 text-gray-700" />

              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700 pointer-events-none">
                <PaperClipIcon className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-md">Drag or click to upload</span>
              </div>
              <div className="text-[10px] mt-2 leading-tight text-center text-gray-500 pointer-events-none space-y-1">
                <p>
                  <span className="font-bold text-gray-700">Accepted formats:</span> png, jpg, jpeg, pdf, docx, xls, xlsx, csv, txt, zip.
                </p>
                {isAdminUser ? (
                  <p className="text-emerald-600 font-medium">
                    <span className="font-bold text-gray-700">Admin Mode:</span> Larger files allowed.
                  </p>
                ) : (
                  <>
                    <p>
                      <span className="font-bold text-gray-700">Maximum size:</span> 1.5 MB per file. Zip files up to 3 MB are allowed.
                    </p>
                    <p className="italic text-red-500">
                      If your file exceeds 1.5 MB, please convert it into a zip file and upload it.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* 📎 List of Attachments — showing individual file sizes and total size */}
            {renderError('attachments')}
            {form.attachments.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs font-semibold text-gray-700 mb-1.5 px-1">
                  <span>Selected Attachments ({form.attachments.length})</span>
                  <span className="text-gray-500 font-normal">
                    Total Size: <strong className="text-gray-800 font-semibold">{formatFileSize(totalAttachmentSize)}</strong>
                  </span>
                </div>
                <ul className="space-y-1.5 text-xs text-left">
                  {form.attachments.map((file, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between gap-2 bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 shadow-sm"
                    >
                      <span className="truncate flex-1 font-medium text-gray-700" title={file.name}>
                        📎 {file.name}
                      </span>
                      {file.size > 0 && (
                        <span className="text-[11px] text-gray-600 font-mono shrink-0 px-1.5 py-0.5 bg-gray-100 rounded border border-gray-200">
                          {formatFileSize(file.size)}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeAttachment(index);
                        }}
                        className="text-red-500 hover:text-red-700 text-xs font-bold shrink-0 ml-1"
                        title="Remove file"
                      >
                        ❌
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Recovery & Restart – DEVELOPER only: Bug → Testing, full-width below Description+Attachments */}
        {(() => {
          const rrRoleId = Number(userInfo?.role_id ?? currentUser?.role_id ?? 0);
          const rrUserId = Number(userInfo?.user_id ?? currentUser?.user_id ?? 0);
          const rrDesig = String(userInfo?.designation_name || currentUser?.designation_name || '').toLowerCase();
          const rrRoleName = String(userInfo?.role_name || currentUser?.role_name || '').toLowerCase();
          const rrIsTester = rrRoleId === 5 || [26, 42].includes(Number(userInfo?.designation_id ?? currentUser?.designation_id ?? 0)) || rrUserId === 101 || rrDesig.includes('test') || rrDesig.includes('qa') || rrRoleName.includes('test') || rrRoleName.includes('qa');
          const isDeveloper = ![1, 2, 3].includes(rrRoleId) && !rrIsTester;
          if (isEditMode && String(form.status) === '8' && form.issue_type === 'Bug' && isDeveloper) {
            return (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                {/* Section header */}
                <p className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <span>🔄</span> Recovery &amp; Restart
                  <span className="text-red-500">*</span>
                  <span className="text-xs font-normal text-gray-400">(Required for Bug → Testing)</span>
                </p>

                {/* Two-column grid — equal-width columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  {/* Column 1 – Problem Recovery */}
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                      <span>🛠</span> Problem Recovery
                      <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <p className="text-[11px] text-gray-400 mb-1 leading-snug">
                      Explain how the issue / bug was identified and resolved.
                    </p>
                    <textarea
                      name="recovery_steps"
                      value={form.recovery_steps}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Describe how the issue was identified, what caused it, and how it was resolved..."
                      className={`block w-full rounded-md px-3 py-2 text-sm border resize-y focus:outline-none focus:ring-2 focus:ring-gray-300 ${errors.recovery_steps ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                        }`}
                    />
                    {renderError('recovery_steps')}
                  </div>

                  {/* Column 2 – Restart Procedure */}
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                      <span>🔁</span> Restart Procedure
                      <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <p className="text-[11px] text-gray-400 mb-1 leading-snug">
                      List the steps followed to restart the system along with that client impact.
                    </p>
                    <textarea
                      name="restart_steps"
                      value={form.restart_steps}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Describe the steps taken to restart the system / service after applying the fix, along with that client impact..."
                      className={`block w-full rounded-md px-3 py-2 text-sm border resize-y focus:outline-none focus:ring-2 focus:ring-gray-300 ${errors.restart_steps ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                        }`}
                    />
                    {renderError('restart_steps')}
                  </div>

                </div>
              </div>
            );
          }
          return null;
        })()}


        <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
          {(onNext || onPrevious) && (
            <div className="flex items-center bg-[#f1f5f7] rounded-xl px-2 py-1 border border-slate-200/80 shadow-xs">
              <button
                type="button"
                onClick={onPrevious}
                disabled={!hasPrevious}
                className="px-3 py-1 rounded-lg hover:bg-white hover:shadow-xs disabled:opacity-30 disabled:hover:bg-transparent transition-all flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-slate-900"
                title="Previous Ticket"
              >
                <FiChevronLeft size={16} /> Prev
              </button>
              <div className="w-[1px] bg-slate-300 mx-1.5 h-4 my-auto" />
              <button
                type="button"
                onClick={onNext}
                disabled={!hasNext}
                className="px-3 py-1 rounded-lg hover:bg-white hover:shadow-xs disabled:opacity-30 disabled:hover:bg-transparent transition-all flex items-center gap-1 text-sm font-bold text-slate-800 hover:text-slate-950"
                title="Next Ticket"
              >
                Next <FiChevronRight size={16} />
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className={`min-w-[150px] inline-flex items-center justify-center px-6 py-2.5 font-bold rounded-xl text-sm text-white shadow-md transition-all active:scale-95 ${submitting ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
              }`}
          >
            {submitting ? "Submitting..." : isEditMode ? "Submit Response" : "Submit Ticket"}
          </button>
        </div>
      </form>
      {/* ⚠️ Front Error Modal Popup */}
      
      {sizePopupMessage && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-fade-in"
          onClick={() => setSizePopupMessage('')}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-sm w-full p-6 text-center transform transition-all scale-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3 border border-red-100 shadow-xs">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-gray-800 mb-1.5">File Limit Exceeded</h3>
            <p className="text-xs text-gray-600 leading-relaxed px-1">
              {sizePopupMessage}
            </p>
            <button
              type="button"
              onClick={() => setSizePopupMessage('')}
              className="w-full mt-5 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl transition shadow-md shadow-red-100 active:scale-95 cursor-pointer"
            >
              OK, Got It
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

